/**
 * F 371 — moving a block to another page.
 *
 * WHY THIS IS A CORE MODULE AND NOT INLINE JSX
 * --------------------------------------------
 * "Move to" is the one action in the new menus that touches TWO pages: it
 * has to remove the block from the page it is on and append it to another
 * page. Both sides are persisted by `pageContentStorage`. Doing that in a
 * click handler buried in a menu component would mean the logic could not
 * be tested without a DOM, and the two-sided write could half-succeed
 * with nobody able to prove which side moved.
 *
 * So the rule is here, as a pure-ish function over the storage module, and
 * the tests drive it directly.
 *
 * THE MOVE, PRECISELY
 * -------------------
 *   1. Read the destination page's blocks (`loadPageContent`).
 *   2. Strip the source block's `id` — ids are per-document. A block that
 *      keeps its id after moving into a different document would collide
 *      with the same id in the source document if the user moved it back.
 *   3. Append the stripped block to the destination's blocks.
 *   4. Write the destination (`savePageContent`).
 *   5. Remove the block from the SOURCE page's blocks and write the source.
 *
 * Steps 4 and 5 are the write. They are ordered so that the destination is
 * written FIRST: if the process dies between them, the block exists in
 * both places (a duplicate) rather than in neither (data loss). A
 * duplicate is recoverable by hand; a lost block is not.
 *
 * NOTHING IS INVENTED
 * -------------------
 * The block shape written to storage is the shape `loadPageContent` returns
 * and the shape `savePageContent` already accepts — that is the existing
 * content format, unchanged. No new entity, no new field, no new storage
 * key, no new concept of "a moved block". A moved block is an ordinary
 * block on the destination page.
 *
 * The `id` strip is the ONLY mutation, and it is required by the format
 * itself: `id` is a document-scoped identity, not a global one.
 */
import {
  hasPageContent,
  loadPageContent,
  savePageContent,
} from '../workspace/pageContentStorage';

/** The minimum shape this module needs from a BlockNote block. */
export interface MovableBlock {
  id?: string;
  type: string;
  props?: Record<string, unknown>;
  content?: unknown;
  children?: unknown[];
}

/**
 * A copy of a block with its document-scoped `id` removed.
 *
 * `children` are stripped of their ids too — they travel with the parent
 * into the same document and would collide the same way.
 */
export function stripBlockIds<T extends MovableBlock>(block: T): T {
  const copy = { ...block } as T & { id?: string; children?: unknown[] };
  delete copy.id;
  if (Array.isArray(copy.children)) {
    copy.children = (copy.children as MovableBlock[]).map((c) =>
      stripBlockIds(c),
    );
  }
  return copy;
}

/**
 * Find a block by id anywhere in a block tree, depth-first.
 */
export function findBlockById(
  blocks: MovableBlock[],
  id: string,
): MovableBlock | null {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (Array.isArray(b.children)) {
      const found = findBlockById(b.children as MovableBlock[], id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Remove a block by id from a block tree, returning a NEW tree.
 *
 * The original array is not mutated — the caller writes the result, and a
 * pure function makes the "did it actually remove it" question
 * answerable in a test.
 */
export function removeBlockById(
  blocks: MovableBlock[],
  id: string,
): { blocks: MovableBlock[]; removed: MovableBlock | null } {
  const removed = findBlockById(blocks, id);
  if (!removed) return { blocks, removed: null };

  const strip = (list: MovableBlock[]): MovableBlock[] =>
    list
      .filter((b) => b.id !== id)
      .map((b) =>
        Array.isArray(b.children)
          ? { ...b, children: strip(b.children as MovableBlock[]) }
          : b,
      );

  return { blocks: strip(blocks), removed };
}

export interface MoveBlockResult {
  ok: boolean;
  /** Why the move did not happen, when `ok` is false. */
  reason?:
    | 'no-such-block'
    | 'same-page'
    | 'destination-unreadable';
  /** The block as written to the destination, with its id stripped. */
  moved?: MovableBlock;
}

/**
 * Move one block from `sourcePageId` to `destinationPageId`.
 *
 * The destination is written FIRST (see the file header for why).
 */
export function moveBlockToPage(
  sourcePageId: string,
  destinationPageId: string,
  blockId: string,
): MoveBlockResult {
  if (sourcePageId === destinationPageId) {
    return { ok: false, reason: 'same-page' };
  }

  const sourceBlocks = (loadPageContent(sourcePageId) ?? []) as MovableBlock[];
  const found = findBlockById(sourceBlocks, blockId);
  if (!found) return { ok: false, reason: 'no-such-block' };

  const moved = stripBlockIds(found);

  const destinationRaw = loadPageContent(destinationPageId);
  if (destinationRaw === null) {
    // `loadPageContent` returns null both for "this page has never been
    // written" and for "this page's content could not be read". Those are
    // DIFFERENT outcomes and must not be conflated: an unwritten page is a
    // perfectly good empty destination, whereas an unreadable one is a
    // failure. `hasPageContent` is the existing API that tells them apart.
    if (hasPageContent(destinationPageId)) {
      return { ok: false, reason: 'destination-unreadable' };
    }
    // Never written ⇒ an empty destination. The moved block becomes the
    // destination page's first block.
    savePageContent(destinationPageId, [moved]);
  } else {
    const destinationBlocks = destinationRaw as MovableBlock[];
    // 1. Destination first — see the file header for why the order is this.
    savePageContent(destinationPageId, [...destinationBlocks, moved]);
  }

  // 2. Source second.
  const removal = removeBlockById(sourceBlocks, blockId);
  if (removal.removed) {
    savePageContent(sourcePageId, removal.blocks);
  }

  return { ok: true, moved };
}
